import { ContractInsightsData } from '@/pages/ContractDetail';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { StatCard, CurrencyDisplay } from '@/components/ui/stat-card';
import { format } from 'date-fns';
import { Shield, TrendingUp, ChevronDown, ExternalLink, CalendarDays, Wallet, FileSignature } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { openSignedUrl } from '@/lib/openSignedUrl';
import { advanceAgreementTarget } from '@/lib/agreement-arc-index';
import { carriesWeekly } from '@/lib/advance-weekly';
import { insuranceAdvancePaid } from '@/lib/insurance-advance-paid';
import { premiumPaidInFull } from '@/lib/insurance-premium-shape';
import { useAgreementArcIndex } from '@/hooks/useAgreements';

interface ContractInsightInsuranceProps {
    data: ContractInsightsData;
}

export default function ContractInsightInsurance({ data }: ContractInsightInsuranceProps) {
    const [expandedPolicy, setExpandedPolicy] = useState<number | null>(null);
    const navigate = useNavigate();
    // The premium advance's own agreement: read in the app where it was signed there, and
    // the admin-filed scan where it was signed on paper. See `advanceAgreementTarget`.
    const arcIndex = useAgreementArcIndex(data.contract?.id ?? null);

    if (!data.insurances || data.insurances.length === 0) {
        return (
            <div className="border border-dashed rounded-xl p-12 text-center bg-muted/5">
                <div className="h-12 w-12 mx-auto rounded-full bg-muted/20 flex items-center justify-center text-muted-foreground">
                    <Shield className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-medium mt-4">No Insurance Policies</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-xs mx-auto">
                    This contract currently has no active insurance policies.
                </p>
            </div>
        );
    }

    // THIS CARD IS THE SUM OF THE POLICY CARDS BENEATH IT, and it is that by construction: one
    // function answers here and on every card below, so the two cannot be taught to count
    // differently from each other. It counted the direct repayments alone and left out the money
    // the engine credits an advance out of the weekly payments — which every policy card has
    // always counted — so the top of the tab read less than the cards under it added up to. See
    // `insuranceAdvancePaid` for the three terms, and for why the deposit joins only a premium
    // the driver paid in full. [D-B58]
    const totalRepaid = data.insurances.reduce((sum, i) => sum + insuranceAdvancePaid(i.loan), 0);

    // Insurance loans principal (loan amount - deposit) - matches Summary tab Value Breakdown.
    //
    // GROSS ON THE PAID-IN-FULL BRANCH, WHICH IS WHERE THE DEPOSIT WENT INTO `totalRepaid`. Net
    // of a deposit that covers the whole premium is nil by construction, so a wholly settled year
    // showed "Total Repayable GHS 0.00" beside a "Repaid" card carrying the premium — the same
    // pair of figures contradicting each other in one row of cards. Taking the premium gross puts
    // the two on the same footing (the admin twin has always read it gross), and the interest
    // reducer below needs no branch: net is nil, so it already adds nothing on this year.
    const insuranceLoansPrincipal = data.insurances.reduce((sum, i) => {
        if (!i.loan) return sum;
        if (premiumPaidInFull(i.loan)) return sum + i.loan.amount;
        return sum + (i.loan.amount - (i.loan.deposit_total || 0));
    }, 0);

    // Insurance loans interest - matches Summary tab Value Breakdown
    const insuranceLoansInterest = data.insurances.reduce((sum, i) => {
        if (!i.loan) return sum;
        const netLoanAmount = i.loan.amount - (i.loan.deposit_total || 0);
        return sum + (netLoanAmount * (i.loan.interest_rate || 0) / 100);
    }, 0);

    return (
        <div className="space-y-8">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <StatCard
                    title="Total Repayable"
                    value={<CurrencyDisplay amount={insuranceLoansPrincipal + insuranceLoansInterest} />}
                    description="Advance + interest owed"
                />
                <StatCard
                    title="Advance"
                    value={<CurrencyDisplay amount={insuranceLoansPrincipal} />}
                    description="Net advance principal"
                />
                <StatCard
                    title="Interest"
                    value={<CurrencyDisplay amount={insuranceLoansInterest} />}
                    description="Total interest owed"
                />
                <StatCard
                    title="Repaid"
                    value={<CurrencyDisplay amount={totalRepaid} />}
                    description="Total amount repaid"
                />
                <StatCard
                    title="Instalment"
                    value={<CurrencyDisplay amount={data.insurances.reduce((sum, insurance) => {
                        if (!insurance.loan) return sum;

                        // WHETHER AN ADVANCE HAS A WEEKLY IS A QUESTION ABOUT ITS SCHEDULE, AND
                        // THE MONEY IS NOT ALLOWED TO ANSWER IT — the `/52` below is a synthesis,
                        // and nobody is charged it for an advance settled at the end of the
                        // contract. See `carriesWeekly` for why the money would have got it
                        // wrong the moment the engine starts crediting those advances. [D-B58]
                        if (!carriesWeekly(insurance.loan)) return sum;

                        // Deduct deposit from loan principal before applying interest rate
                        const netLoanPrincipal = insurance.loan.amount - (insurance.loan.deposit_total || 0);
                        const loanTotal = netLoanPrincipal + (netLoanPrincipal * (insurance.loan.interest_rate || 0) / 100);
                        const totalPaid = insuranceAdvancePaid(insurance.loan);
                        // Use 99.9% threshold to handle floating point precision
                        const isPaidOff = totalPaid >= loanTotal * 0.999;
                        if (isPaidOff) return sum;
                        // Calculate weekly instalment as total / 52 weeks
                        const weeklyInstalment = loanTotal / 52;
                        return sum + weeklyInstalment;
                    }, 0)} />}
                    description="Active advances weekly"
                />
            </div>

            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold tracking-tight">Policies</h3>
                </div>

                <div className="space-y-4">
                    {data.insurances.map((insurance) => {
                        const isExpanded = expandedPolicy === insurance.id;
                        // WHICH INSTRUMENT THIS YEAR IS, read the way the server reads it — the
                        // deposits cover the premium and there is no instalment row, so nothing
                        // was advanced and nothing is owed. See `premiumPaidInFull`.
                        const paidInFull = premiumPaidInFull(insurance.loan);
                        // Deduct deposit from loan principal before applying interest rate
                        const netLoanPrincipal = insurance.loan
                            ? insurance.loan.amount - (insurance.loan.deposit_total || 0)
                            : 0;
                        // BOTH SIDES OF THE RATIO COUNT THE PREMIUM THE SAME WAY. On the
                        // paid-in-full branch the deposit joins the numerator below, so the
                        // denominator takes the premium GROSS — the admin twin's reading, and
                        // the only one that is not self-contradictory here: net of a deposit
                        // that covers the whole premium is nil, so the card printed "Total
                        // Repayable GHS 0.00" over "Repaid GHS 3,977.00". Gross, the two
                        // figures are the same number and the percentage falls out at 100
                        // arithmetically, with no special case to keep in step with the rest.
                        // No interest arises on this branch either, which is what net-of-deposit
                        // interest already says: nil times any rate.
                        const loanTotal = !insurance.loan
                            ? 0
                            : paidInFull
                                ? insurance.loan.amount
                                : netLoanPrincipal + (netLoanPrincipal * (insurance.loan.interest_rate || 0) / 100);
                        // THE SAME FIGURE THE "REPAID" STAT ABOVE SUMS, from the same function —
                        // the direct repayments, the money credited out of the weekly payments,
                        // and the deposit on a premium paid in full and nowhere else.
                        const totalPaid = insuranceAdvancePaid(insurance.loan);
                        const repaidPercent =
                            loanTotal > 0 ? Math.min(100, (totalPaid / loanTotal) * 100) : 0;
                        // Use 99.9% threshold to handle floating point precision from interest calculations
                        const isPaidOff = totalPaid >= loanTotal * 0.999 && loanTotal > 0;
                        // SAME QUESTION, SAME ANSWER AS THE STAT CARD ABOVE. This drives the badge
                        // below and the striped fill on the progress bar, both of which describe
                        // the arrangement rather than the balance. See `carriesWeekly`. [D-B58]
                        const hasInstalment = carriesWeekly(insurance.loan);
                        const agreementTarget = insurance.loan
                            ? advanceAgreementTarget(
                                  arcIndex.data?.insuranceLoans.get(insurance.loan.id) ?? null,
                                  insurance.loan.agreement_url,
                              )
                            : null;

                        return (
                            <motion.div
                                layout
                                key={insurance.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.2 }}
                            >
                                <Card
                                    className={cn(
                                        "overflow-hidden border shadow-sm transition-all duration-200",
                                        isExpanded ? "ring-1 ring-primary/20 shadow-md" : "hover:border-primary/20 hover:shadow-md"
                                    )}

                                >
                                    <div
                                        className="relative cursor-pointer group"
                                        onClick={() => setExpandedPolicy(isExpanded ? null : insurance.id)}
                                    >
                                        <div className="p-5">
                                            {/* Header Section */}
                                            <div className="flex items-start gap-4 mb-4">
                                                {/* Icon Box */}
                                                <div className={cn(
                                                    "p-3 rounded-2xl transition-colors shrink-0 flex items-center justify-center",
                                                    isPaidOff
                                                        ? "bg-emerald-100/60 text-emerald-700 dark:bg-emerald-900/30"
                                                        : "bg-primary/10 text-primary dark:bg-primary/20"
                                                )}>
                                                    <Shield className="h-6 w-6" />
                                                </div>

                                                {/* Title & Actions Row */}
                                                <div className="flex-1 min-w-0 pt-0.5">
                                                    <div className="flex items-start justify-between gap-2 mb-1.5">
                                                        <h4 className="font-semibold text-lg text-foreground truncate">
                                                            Policy #{insurance.id}
                                                        </h4>

                                                        {/* Actions Dropdown Chevron */}
                                                        <div className={cn(
                                                            "w-8 h-8 flex items-center justify-center rounded-full transition-transform duration-200 shrink-0 -mt-1 -mr-2 cursor-pointer",
                                                            isExpanded ? "bg-muted rotate-180" : "hover:bg-muted"
                                                        )}>
                                                            <ChevronDown className="h-5 w-5 text-muted-foreground" />
                                                        </div>
                                                    </div>

                                                    <div className="flex flex-wrap items-center gap-2 mb-2">
                                                        {insurance.loan && (
                                                            <Badge
                                                                variant="secondary"
                                                                className={cn(
                                                                    "font-medium px-2.5 py-0.5",
                                                                    isPaidOff
                                                                        ? "bg-emerald-100/80 text-emerald-800 hover:bg-emerald-100/80"
                                                                        : "bg-blue-50 text-blue-800 hover:bg-blue-50 border border-blue-100"
                                                                )}
                                                            >
                                                                {/* NEITHER "ADVANCE" WORD IS TRUE OF A YEAR THE
                                                                    DRIVER PAID: "Active Advance" describes one
                                                                    running and "Advance Paid" one repaid, and
                                                                    Primelane advanced nothing here. */}
                                                                {paidInFull
                                                                    ? 'Premium paid in full'
                                                                    : isPaidOff
                                                                        ? 'Advance Paid'
                                                                        : 'Active Advance'}
                                                            </Badge>
                                                        )}
                                                        {/* NO WEEKLY IS AN ARRANGEMENT, NOT AN OVERSIGHT. On a
                                                            contract whose hire purchase agreement leaves no
                                                            room for an advance instalment, the advance is
                                                            settled at the end of the contract instead — so
                                                            amber warned a driver about a term of their own
                                                            contract, on the one shape where the absence is
                                                            the arrangement. A Record has no instalment by
                                                            construction either, and is excluded above. [D-B57]

                                                            AN ADVANCE THAT HAS BEEN RECOVERED IS NOT WAITING
                                                            FOR THE END OF ANYTHING. Once the payments made
                                                            after the vehicle is paid for clear it, the badge
                                                            beside this one reads "Advance Paid" and there is
                                                            nothing left for this one to describe. The admin
                                                            has always carried that limb; with it here the two
                                                            apps say one thing about one advance. [D-B58] */}
                                                        {insurance.loan && !hasInstalment && !paidInFull && !isPaidOff && (
                                                            <Badge
                                                                variant="outline"
                                                                className="font-medium px-2.5 py-0.5 text-muted-foreground"
                                                            >
                                                                Settled at end of contract
                                                            </Badge>
                                                        )}
                                                    </div>

                                                    {/* Meta details (Dates, Value) */}
                                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
                                                        {insurance.starts_at && (
                                                            <div className="flex items-center gap-1.5">
                                                                <CalendarDays className="h-4 w-4 opacity-70" />
                                                                <span>{format(new Date(insurance.starts_at), 'MMM d, yyyy')}</span>
                                                                {insurance.expires_at && (
                                                                    <>
                                                                        <span className="text-muted-foreground/50 mx-1">-</span>
                                                                        <span>{format(new Date(insurance.expires_at), 'MMM d, yyyy')}</span>
                                                                    </>
                                                                )}
                                                            </div>
                                                        )}
                                                        {insurance.valued_at && (
                                                            <div className="flex items-center gap-1.5">
                                                                <span className="w-1 h-1 rounded-full bg-muted-foreground/30 hidden sm:block" />
                                                                <span>Value: <span className="font-medium text-foreground"><CurrencyDisplay amount={insurance.valued_at} /></span></span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Document Buttons */}
                                                    <div className="flex flex-wrap items-center gap-2 mt-3">
                                                        {insurance.certificate_url && (
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="h-8 text-xs font-medium"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openSignedUrl(insurance.certificate_url ?? undefined);
                                                                }}
                                                            >
                                                                <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                                                                Certificate
                                                            </Button>
                                                        )}
                                                        {agreementTarget && (
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="h-8 text-xs font-medium"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (agreementTarget.kind === 'viewer') {
                                                                        navigate(`/agreement/${agreementTarget.documentId}`);
                                                                    } else {
                                                                        void openSignedUrl(agreementTarget.url);
                                                                    }
                                                                }}
                                                            >
                                                                <FileSignature className="h-3.5 w-3.5 mr-1.5" />
                                                                {agreementTarget.kind === 'viewer'
                                                                    ? 'Agreement'
                                                                    : 'Agreement scan'}
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Key Stats Grid */}
                                            <div className="bg-muted/30 rounded-xl p-4 mb-2">
                                                <div className="grid grid-cols-2 gap-y-4 gap-x-6">

                                                    {insurance.loan && (
                                                        <div>
                                                            <p className="text-xs font-medium text-muted-foreground mb-1 uppercase tracking-wider">Total Repayable</p>
                                                            <p className="text-sm font-semibold text-foreground"><CurrencyDisplay amount={loanTotal} /></p>
                                                        </div>
                                                    )}
                                                    {insurance.loan && (
                                                        <div className="col-span-2">
                                                            <div className="flex items-end justify-between mb-1.5">
                                                                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Repayment Progress</p>
                                                                <div className="flex items-baseline gap-1.5">
                                                                    <p className="text-sm font-bold text-foreground"><CurrencyDisplay amount={totalPaid} /></p>
                                                                    <span className="text-xs font-medium text-muted-foreground">({repaidPercent.toFixed(0)}%)</span>
                                                                </div>
                                                            </div>

                                                            {/* Progress Bar inside Stats Grid */}
                                                            <div className="h-2 bg-background rounded-full overflow-hidden relative border border-border/50">
                                                                {/* Striped background for no-instalment policies */}
                                                                {!hasInstalment && !isPaidOff && (
                                                                    <div
                                                                        className="absolute inset-0 opacity-40 bg-[length:12px_12px]"
                                                                        style={{
                                                                            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 4px, hsl(var(--muted-foreground)) 4px, hsl(var(--muted-foreground)) 5px)',
                                                                            left: `${repaidPercent}%`,
                                                                            width: `${100 - repaidPercent}%`
                                                                        }}
                                                                    />
                                                                )}
                                                                <div
                                                                    className={cn("h-full transition-all duration-500 relative z-10",
                                                                        isPaidOff ? "bg-emerald-500" : "bg-primary"
                                                                    )}
                                                                    style={{ width: `${repaidPercent}%` }}
                                                                />
                                                            </div>

                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Expanded Details */}
                                    <AnimatePresence>
                                        {isExpanded && insurance.loan && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.2 }}
                                            >
                                                <CardContent className="px-5 pb-5 pt-0 border-t bg-muted/5">
                                                    <div className="pt-5">
                                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                                                            <div className="bg-background rounded-lg p-3 border shadow-sm">
                                                                {/* `insurance_loans.amount` is the whole premium on a
                                                                    year the driver paid in full, and calling that
                                                                    figure "Advance" contradicts the badge above it. */}
                                                                <p className="text-xs text-muted-foreground mb-1">{paidInFull ? 'Premium' : 'Advance'}</p>
                                                                <p className="font-semibold text-sm"><CurrencyDisplay amount={insurance.loan.amount} /></p>
                                                            </div>
                                                            <div className="bg-background rounded-lg p-3 border shadow-sm">
                                                                <p className="text-xs text-muted-foreground mb-1">Deposit Paid</p>
                                                                <p className="font-semibold text-sm"><CurrencyDisplay amount={insurance.loan.deposit_total} /></p>
                                                            </div>
                                                            <div className="bg-background rounded-lg p-3 border shadow-sm">
                                                                <p className="text-xs text-muted-foreground mb-1">Interest Rate</p>
                                                                <p className="font-semibold text-sm flex items-center gap-1">
                                                                    {insurance.loan.interest_rate || 0}%
                                                                    <TrendingUp className="h-3 w-3 text-muted-foreground" />
                                                                </p>
                                                            </div>
                                                            <div className="bg-background rounded-lg p-3 border shadow-sm">
                                                                <p className="text-xs text-muted-foreground mb-1">Agreement Signed</p>
                                                                <p className="font-semibold text-sm">
                                                                    {insurance.starts_at ? format(new Date(insurance.starts_at), 'dd MMM yyyy') : '-'}
                                                                    {insurance.expires_at && <span className="text-muted-foreground ml-1">to {format(new Date(insurance.expires_at), 'dd MMM yyyy')}</span>}
                                                                </p>
                                                            </div>
                                                        </div>

                                                        {/* Direct Payments and Contract Allocations */}
                                                        {insurance.loan && (
                                                            <div>
                                                                <div className="flex items-center justify-between mb-3">
                                                                    <h5 className="text-sm font-semibold flex items-center gap-2">
                                                                        <Wallet className="h-4 w-4 text-muted-foreground" />
                                                                        Direct Payments
                                                                    </h5>
                                                                </div>
                                                                {insurance.loan.repayments && insurance.loan.repayments.length > 0 ? (
                                                                    <div className="bg-background rounded-xl border divide-y overflow-hidden max-h-[150px] overflow-y-auto mb-4">
                                                                        {insurance.loan.repayments.map((repayment) => (
                                                                            <div key={repayment.id} className="flex justify-between items-center px-4 py-2.5 hover:bg-muted/30 transition-colors group/row">
                                                                                <div className="flex flex-col">
                                                                                    <span className="text-sm font-medium flex items-center gap-2">
                                                                                        {format(new Date(repayment.paid_at), 'MMM d, yyyy')}
                                                                                        {repayment.is_deposit && (
                                                                                            <Badge variant="outline" className="text-xs px-1.5 py-0 h-4 bg-purple-50 text-purple-600 border-purple-200">
                                                                                                Deposit
                                                                                            </Badge>
                                                                                        )}
                                                                                    </span>
                                                                                    <span className="text-xs text-muted-foreground">
                                                                                        {repayment.description || (repayment.is_deposit ? 'Insurance Deposit' : 'Repayment')}
                                                                                    </span>
                                                                                </div>
                                                                                <div className="flex items-center gap-2">
                                                                                    <Badge variant="outline" className="text-emerald-600 bg-emerald-50 border-emerald-100 font-mono">
                                                                                        +<CurrencyDisplay amount={repayment.amount} />
                                                                                    </Badge>
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                ) : (
                                                                    <div className="text-center py-4 border-2 border-dashed rounded-xl mb-4">
                                                                        <p className="text-xs text-muted-foreground">No direct payments yet.</p>
                                                                    </div>
                                                                )}

                                                                {/* Contract Allocations */}
                                                                <div className="flex items-center justify-between mb-3">
                                                                    <h5 className="text-sm font-semibold flex items-center gap-2">
                                                                        <Wallet className="h-4 w-4 text-muted-foreground" />
                                                                        Contract Allocations
                                                                        <span className="text-xs text-muted-foreground font-normal">
                                                                            (from weekly payments)
                                                                        </span>
                                                                    </h5>
                                                                </div>
                                                                {insurance.loan.recent_allocations && insurance.loan.recent_allocations.length > 0 ? (
                                                                    <div className="space-y-2">
                                                                        <div className="bg-background rounded-xl border divide-y overflow-hidden">
                                                                            {insurance.loan.recent_allocations.slice(0, 5).map((alloc) => (
                                                                                <div key={alloc.repayment_id} className="flex justify-between items-center px-4 py-2.5 hover:bg-muted/30 transition-colors">
                                                                                    <div className="flex flex-col">
                                                                                        <span className="text-sm font-medium">
                                                                                            {format(new Date(alloc.payment_date), 'MMM d, yyyy')}
                                                                                        </span>
                                                                                        <span className="text-xs text-muted-foreground">
                                                                                            from <CurrencyDisplay amount={alloc.payment_amount} /> payment
                                                                                        </span>
                                                                                    </div>
                                                                                    <Badge variant="outline" className="text-amber-600 bg-amber-50 border-amber-100 font-mono">
                                                                                        →<CurrencyDisplay amount={alloc.amount} />
                                                                                    </Badge>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div className="text-center py-4 border-2 border-dashed rounded-xl">
                                                                        <p className="text-xs text-muted-foreground">No allocations from contract repayments yet.</p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </CardContent>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </Card>
                            </motion.div>
                        );
                    })}
                </div>
            </div >

        </div >
    );
}
